import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { Flex, Pressable } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import EmptyState from '@atlaskit/empty-state/empty-state';
import { Label } from '@atlaskit/form/label/default';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import DeleteIcon from '@atlaskit/icon/core/delete';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import TranslateIcon from '@atlaskit/icon/core/translate';
import IconTile from '@atlaskit/icon/icon-tile';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select/default';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-bridge/constants';

import { CollapsibleItem } from '../../collapsible-item';
import { ExtensionsConfigWrapper } from '../../extensions-config-wrapper';

import { useHubTranslationsContext } from './hub-translations-context';
import { useManageTranslationsOpenContext } from './manage-translations-open-context';
import { messages } from './messages';
import { useBlockTranslations } from './use-block-translations';
import {
	buildTranslationRows,
	type ManageTranslationsConfig,
	type TranslationFormRow,
} from './utils';

const styles = cssMap({
	localeSection: {
		paddingBottom: token('space.300'),
	},
	defaultLocaleRow: {
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	stickyFooter: {
		zIndex: 1,
		position: 'sticky',
		bottom: token('space.0'),
		paddingBlock: token('space.300'),
		backgroundColor: token('elevation.surface'),
	},
	defaultLocaleIcon: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	emptyStateImage: {
		marginBottom: token('space.200'),
	},
	removeButton: {
		alignItems: 'center',
		borderRadius: token('radius.small'),
		display: 'flex',
		height: '32px',
		justifyContent: 'center',
		'&:hover': {
			backgroundColor: token('color.background.accent.red.subtlest.hovered'),
			color: token('color.text.danger'),
		},
		'&:focus': {
			backgroundColor: token('color.background.accent.red.subtlest.hovered'),
			color: token('color.text.danger'),
		},
		transition: token('motion.button.hovered'),
	},
	addButtonContentWrapper: {
		height: '20px',
	},
});

type LocaleOption = {
	label: string;
	value: string;
};

type ManageTranslationsProps<TParameters extends Record<string, unknown>> = {
	closeConfigPanel: () => void;
	config?: ManageTranslationsConfig;
	/**
	 * The ADF `extensionType` of the block (e.g. `"com.atlassian.confluence.carousel"`).
	 */
	extensionType: string;
	itemId?: string;
	/**
	 * The ADF `localId` of the block — the stable anchor that identifies this extension
	 * instance within the layout.
	 */
	localId: string;
	parameters: TParameters | null | undefined;
};

type ManageTranslationsInnerProps<TParameters extends Record<string, unknown>> = Omit<
	ManageTranslationsProps<TParameters>,
	'parameters'
> & {
	parameters: TParameters;
};

const ManageTranslationsInner = <TParameters extends Record<string, unknown>>({
	config,
	parameters,
	closeConfigPanel,
	itemId,
	localId,
	extensionType,
}: ManageTranslationsInnerProps<TParameters>): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [translationLocales, setTranslationLocales] = useState<string[]>([]);
	const [nextLocale, setNextLocale] = useState<LocaleOption | null>(null);
	const [isLanguageSelectorVisible, setIsLanguageSelectorVisible] = useState(false);
	const [recentlyAddedLocale, setRecentlyAddedLocale] = useState<string | null>(null);
	const openContext = useManageTranslationsOpenContext();
	const handleOpenChange = openContext?.setIsOpen;
	const activeItemId = openContext?.activeItemId ?? null;
	const setHasTranslationChanges = openContext?.setHasTranslationChanges;
	const hubTranslationsCtx = useHubTranslationsContext();
	const setPageIsDirty = hubTranslationsCtx?.setPageIsDirty;
	const storedTranslations = useMemo(
		() => hubTranslationsCtx?.storedTranslations ?? {},
		[hubTranslationsCtx],
	);
	const supportedTranslationLanguages = useMemo(
		() => hubTranslationsCtx?.supportedLanguages ?? {},
		[hubTranslationsCtx],
	);
	const defaultLocale = hubTranslationsCtx?.defaultLocale ?? '';
	const defaultLocaleDisplayName = hubTranslationsCtx?.defaultLocaleDisplayName;
	const { stampExtensionType, ensureBlockEntry, setFieldValue, removeLocale } =
		useBlockTranslations({
			localId,
			extensionType,
		});

	const getTranslationLocaleLabel = useCallback(
		(localeCode: string): string =>
			supportedTranslationLanguages[localeCode]?.localeDisplayName ?? localeCode,
		[supportedTranslationLanguages],
	);

	const rows = useMemo<TranslationFormRow[]>(() => {
		const allRows = buildTranslationRows({
			config,
			parameters,
			itemId,
		});

		// When scoped to a specific item, only surface that item's fields so the
		// manage-translations view shows the fields for the clicked card only, not for
		// every card (and not the extension-level scalar fields).
		if (activeItemId !== null) {
			return allRows.filter((row) => row.itemId === activeItemId);
		}

		return allRows;
	}, [config, parameters, itemId, activeItemId]);

	/**
	 * Builds the concrete fieldPath for the BE mutation from a row.
	 * Array-item rows use cardId-based paths; scalar rows use the path as-is.
	 */
	const buildConcreteFieldPath = useCallback((row: TranslationFormRow): string => {
		if (row.itemId !== undefined) {
			const parts = row.id.split('.');
			const arrayKey = parts[0];
			const fieldSegments = parts.slice(2).join('.');
			return `${arrayKey}.${row.itemId}.${fieldSegments}`;
		}
		return row.id;
	}, []);

	const initialStoredTranslationsRef = React.useRef(storedTranslations);
	useEffect(() => {
		// Filter out locales with no filled fields for the visible rows
		const initialStoredTranslations = initialStoredTranslationsRef.current;
		const storedLocales = Object.keys(initialStoredTranslations).filter((loc) => {
			if (fg('hub_translations_save_default_lang') && loc === defaultLocale) {
				return false;
			}
			const blockFields = initialStoredTranslations[loc]?.[localId]?.fields;
			if (!blockFields) {
				return false;
			}
			return rows.some((row) => {
				const concreteFieldPath = buildConcreteFieldPath(row);
				return (blockFields[concreteFieldPath] ?? '').trim() !== '';
			});
		});

		// Stamp extensionType from props onto all existing block entries for this localId.
		// This is the authoritative source — always overwrite so that even pre-codegen
		// empty values and future type changes are corrected on mount.
		const localesWithBlock = Object.keys(initialStoredTranslations).filter((loc) => {
			if (fg('hub_translations_save_default_lang') && loc === defaultLocale) {
				return false;
			}
			return initialStoredTranslations[loc]?.[localId] !== undefined;
		});
		stampExtensionType(localesWithBlock);

		if (storedLocales.length === 0) {
			return;
		}

		setTranslationLocales((currentLocales) => {
			const merged = [...currentLocales];
			for (const storedLocale of storedLocales) {
				if (!merged.includes(storedLocale)) {
					merged.push(storedLocale);
				}
			}
			return merged;
		});

		setIsLanguageSelectorVisible(false);
		// This effect intentionally runs only once on mount — deps are captured via ref above.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleFieldChange = useCallback(
		(translationLocale: string, rowId: string, value: string) => {
			const matchedRow = rows.find((r) => r.id === rowId);
			// Use the concrete fieldPath for storage; fall back to rowId for unknown rows.
			const concreteFieldPath = matchedRow ? buildConcreteFieldPath(matchedRow) : rowId;
			setFieldValue(translationLocale, concreteFieldPath, value);
			// Notify the context (and therefore the action bar) that there are unsaved
			// translation changes so the Publish button becomes enabled.
			setHasTranslationChanges?.(true);
			setPageIsDirty?.(true);
		},
		[setFieldValue, setHasTranslationChanges, rows, buildConcreteFieldPath, setPageIsDirty],
	);

	const languageOptions = useMemo<LocaleOption[]>(
		() =>
			Object.keys(supportedTranslationLanguages)
				.filter(
					(supportedLocale) =>
						supportedLocale !== locale && !translationLocales.includes(supportedLocale),
				)
				.map((supportedLocale) => ({
					label: supportedTranslationLanguages[supportedLocale].localeDisplayName,
					value: supportedLocale,
				})),
		[locale, translationLocales, supportedTranslationLanguages],
	);

	const addTranslationLocale = useCallback(
		(translationLocale: string) => {
			setTranslationLocales((currentLocales) => [translationLocale, ...currentLocales]);
			setRecentlyAddedLocale(translationLocale);
			// Ensure a (possibly empty) block entry exists in storedTranslations for the
			// new locale so the form renders empty fields immediately.
			ensureBlockEntry(translationLocale);
		},
		[ensureBlockEntry],
	);

	const handleAddTranslationClick = useCallback(() => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'addTranslation',
			analyticsType: UI_EVENT_TYPE,
			attributes: { extensionType },
		}).fire();
		setIsLanguageSelectorVisible(true);
	}, [createAnalyticsEvent, extensionType]);

	const handleLanguageSelection = useCallback(
		(selectedOption: LocaleOption | null) => {
			setNextLocale(selectedOption);
			if (!selectedOption) {
				return;
			}

			addTranslationLocale(selectedOption.value);
			setNextLocale(null);
			setIsLanguageSelectorVisible(false);
			createAnalyticsEvent({
				action: 'changes',
				actionSubject: 'select',
				actionSubjectId: 'languageSelection',
				analyticsType: UI_EVENT_TYPE,
				attributes: { locale: selectedOption.value, extensionType },
			}).fire();
		},
		[addTranslationLocale, createAnalyticsEvent, extensionType],
	);

	const handleRemoveTranslation = useCallback(
		(translationLocale: string) => {
			setTranslationLocales((currentLocales) => {
				const nextLocales = currentLocales.filter(
					(currentLocale) => currentLocale !== translationLocale,
				);
				if (nextLocales.length === 0) {
					setIsLanguageSelectorVisible(false);
				}
				return nextLocales;
			});
			// Remove this block's data for the deleted locale from the shared context store.
			// Other blocks' data for this locale (different localIds) is preserved.
			removeLocale(translationLocale);
			if (recentlyAddedLocale === translationLocale) {
				setRecentlyAddedLocale(null);
			}
			setHasTranslationChanges?.(true);
			setPageIsDirty?.(true);
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'removeTranslation',
				analyticsType: UI_EVENT_TYPE,
				attributes: { locale: translationLocale, extensionType },
			}).fire();
		},
		[
			recentlyAddedLocale,
			removeLocale,
			setHasTranslationChanges,
			createAnalyticsEvent,
			extensionType,
			setPageIsDirty,
		],
	);

	const renderTranslationField = (translationLocale: string, row: TranslationFormRow) => {
		// For each input field, we use a unique id that includes the translation locale and the field id.
		const inputId = `manage-translations-${translationLocale}-${row.id.replace(/[^a-zA-Z0-9-_:.]/g, '-')}`;
		// Read the displayed value from storedTranslations using the concrete fieldPath.
		const concreteFieldPath = buildConcreteFieldPath(row);
		const fieldValue =
			storedTranslations[translationLocale]?.[localId]?.fields[concreteFieldPath] ?? '';

		return (
			<Stack space="space.050" key={`${translationLocale}-${row.id}`}>
				<Label htmlFor={inputId}>
					<Text color="color.text">{row.fieldLabel}</Text>
				</Label>
				<Textfield
					id={inputId}
					value={fieldValue}
					onChange={(event) =>
						handleFieldChange(translationLocale, row.id, event.currentTarget.value)
					}
					placeholder={row.placeholder}
				/>
			</Stack>
		);
	};

	const languageSelector = (
		<CollapsibleItem title={formatMessage(messages.selectLanguage)} defaultIsExpanded={true}>
			<Stack space="space.150" xcss={styles.localeSection}>
				<Label htmlFor="manage-translations-locale-select">
					<Text color="color.text">
						<FormattedMessage {...messages.languageFieldLabel} />
					</Text>
				</Label>
				<Select<LocaleOption, false>
					inputId="manage-translations-locale-select"
					value={nextLocale}
					onChange={(selectedOption) =>
						handleLanguageSelection((selectedOption as LocaleOption | null) ?? null)
					}
					options={languageOptions}
					placeholder={formatMessage(messages.selectLanguage)}
				/>
			</Stack>
		</CollapsibleItem>
	);

	return (
		<ErrorBoundary fallback={null}>
			<ExtensionsConfigWrapper
				buttonIcon={
					<IconButton
						appearance="subtle"
						icon={ArrowLeftIcon}
						label={<FormattedMessage {...messages.back} />}
						onClick={() => handleOpenChange?.(false)}
					/>
				}
				title={<FormattedMessage {...messages.panelTitle} />}
				closeConfigPanel={() => closeConfigPanel()}
				footerComponent={() =>
					!isLanguageSelectorVisible &&
					translationLocales.length > 0 &&
					languageOptions.length > 0 && (
						<Box xcss={styles.stickyFooter}>
							<Stack space="space.100">
								{languageOptions.length > 0 && (
									<Button
										appearance="primary"
										onClick={handleAddTranslationClick}
										shouldFitContainer
										isDisabled={isLanguageSelectorVisible}
									>
										<Flex
											alignItems="center"
											justifyContent="center"
											gap="space.050"
											xcss={styles.addButtonContentWrapper}
										>
											<AddIcon color="currentColor" label="" />
											<FormattedMessage {...messages.addTranslation} />
										</Flex>
									</Button>
								)}
							</Stack>
						</Box>
					)
				}
			>
				<Stack space="space.200">
					<Text color="color.text.subtle">
						<FormattedMessage {...messages.description} />
					</Text>
					<Stack space="space.050">
						<Inline xcss={styles.defaultLocaleRow}>
							<Box xcss={styles.defaultLocaleIcon}>
								<StatusInformationIcon
									label={formatMessage(messages.defaultLanguageIconLabel)}
									color={token('color.icon.accent.purple')}
								/>
							</Box>
							<Text color="color.text">
								<FormattedMessage
									{...messages.defaultLanguageDescription}
									values={{
										defaultLanguage:
											defaultLocaleDisplayName ?? getTranslationLocaleLabel(defaultLocale),
									}}
								/>
							</Text>
						</Inline>
					</Stack>
					{translationLocales.length === 0 && !isLanguageSelectorVisible ? (
						<EmptyState
							header={formatMessage(messages.emptyStateHeader)}
							description={formatMessage(messages.emptyStateDescription)}
							primaryAction={
								<Button
									appearance="primary"
									onClick={handleAddTranslationClick}
									testId="manage-translations-empty-state-add-button"
								>
									<Flex
										alignItems="center"
										justifyContent="center"
										gap="space.050"
										xcss={styles.addButtonContentWrapper}
									>
										<AddIcon color="currentColor" label="" />
										<FormattedMessage {...messages.addTranslation} />
									</Flex>
								</Button>
							}
							testId="manage-translations-empty-state"
							renderImage={() => (
								<Box xcss={styles.emptyStateImage}>
									<IconTile icon={TranslateIcon} label="" appearance="gray" />
								</Box>
							)}
						/>
					) : null}
					{isLanguageSelectorVisible ? languageSelector : null}
					{translationLocales.length > 0 && (
						<Stack space="space.200">
							{translationLocales.map((translationLocale) => (
								<CollapsibleItem
									key={translationLocale}
									title={getTranslationLocaleLabel(translationLocale)}
									defaultIsExpanded={translationLocale === recentlyAddedLocale}
								>
									<Stack space="space.200">
										{rows.map((row) => renderTranslationField(translationLocale, row))}
										<Pressable
											onClick={() => handleRemoveTranslation(translationLocale)}
											xcss={styles.removeButton}
										>
											<Flex columnGap="space.050" alignItems="center">
												<DeleteIcon label="" color="currentColor" />
												<FormattedMessage {...messages.removeTranslation} />
											</Flex>
										</Pressable>
									</Stack>
								</CollapsibleItem>
							))}
						</Stack>
					)}
				</Stack>
			</ExtensionsConfigWrapper>
		</ErrorBoundary>
	);
};

export const ManageTranslations = <TParameters extends Record<string, unknown>>({
	parameters,
	...props
}: ManageTranslationsProps<TParameters>): React.JSX.Element | null => {
	return parameters ? <ManageTranslationsInner parameters={parameters} {...props} /> : null;
};

export {
	ManageTranslationsOpenProvider,
	useManageTranslationsOpen,
	useManageTranslationsOpenContext,
	type ManageTranslationsOpenContextValue,
	type ManageTranslationsOpenProviderProps,
} from './manage-translations-open-context';
export { ManageTranslationsButton } from './manage-translations-button/manage-translations-button';
